import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Globe, Linkedin, Twitter } from "lucide-react";
import { Mascot } from "@/components/Mascot";

// Deterministic color per expert (from brand palette)
const PALETTE = ["#FF8C42", "#FFA85C", "#FFD600", "#4EA8FF", "#E56E1F"];
function colorFor(id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % PALETTE.length;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function initialsOf(name) {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function ExpertsPage() {
  const [experts, setExperts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const q = filter === "all" ? "" : `?country=${filter}`;
      const { data } = await api.get(`/experts${q}`);
      setExperts(data.experts || []);
      setLoading(false);
    })();
  }, [filter]);

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <div className="flex items-start gap-6 mb-10">
        <div className="flex-1">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FF8C42] mb-3">
            Répertoire · SEO Experts
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tight leading-none" data-testid="experts-title">
            Les voix qui définissent le SEO.
          </h1>
          <p className="text-[#FAF0DE]/50 mt-3 max-w-2xl text-lg">
            30+ experts internationaux et francophones sélectionnés pour leur influence,
            leur rigueur et l'originalité de leurs analyses.
          </p>
        </div>
        <Mascot pose="waving" className="hidden md:block w-28 h-28 shrink-0" />
      </div>

      <div className="flex items-center gap-2 mb-8">
        {[
          { k: "all", l: "Tous" },
          { k: "international", l: "International 🌍" },
          { k: "france", l: "France 🇫🇷" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`expert-filter-${f.k}`}
            className={`text-xs font-mono uppercase tracking-widest px-4 py-2 rounded-full border transition-all ${
              filter === f.k ? "bg-[#FF8C42] text-[#0A1225] border-[#FF8C42]" : "text-[#FAF0DE]/60 border-[#FAF0DE]/10 hover:border-[#FAF0DE]/30 hover:text-[#FAF0DE]"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#FAF0DE]/40 font-mono text-sm">Chargement…</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {experts.map((e) => {
            const color = colorFor(e.expert_id);
            return (
              <div
                key={e.expert_id}
                className="card-lift rounded-3xl border border-[#FAF0DE]/8 bg-[#132038]/60 backdrop-blur p-6 hover:border-[#FAF0DE]/20 transition-all"
                data-testid={`expert-card-${e.expert_id}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center font-display text-lg font-black shrink-0"
                    style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}
                  >
                    {initialsOf(e.name)}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-lg leading-tight">{e.name}</div>
                    <div className="text-xs text-[#FAF0DE]/50 mt-1">{e.role}</div>
                  </div>
                </div>
                <p className="text-sm text-[#FAF0DE]/60 leading-relaxed mb-4 line-clamp-3">{e.bio}</p>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {e.specialties.slice(0, 3).map((s, j) => (
                    <span key={j} className="text-[10px] font-mono uppercase tracking-widest text-[#FAF0DE]/50 border border-[#FAF0DE]/10 rounded-full px-2.5 py-0.5">
                      {s}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-3 text-[#FAF0DE]/40 pt-3 border-t border-[#FAF0DE]/5">
                  {e.twitter && (
                    <a href={e.twitter} target="_blank" rel="noopener noreferrer" className="hover:text-[#FF8C42]" data-testid={`expert-twitter-${e.expert_id}`}>
                      <Twitter className="w-4 h-4" />
                    </a>
                  )}
                  {e.linkedin && (
                    <a href={e.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-[#FF8C42]" data-testid={`expert-linkedin-${e.expert_id}`}>
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                  {e.website && (
                    <a href={e.website} target="_blank" rel="noopener noreferrer" className="hover:text-[#FF8C42]" data-testid={`expert-website-${e.expert_id}`}>
                      <Globe className="w-4 h-4" />
                    </a>
                  )}
                  <span className="ml-auto text-[10px] font-mono uppercase tracking-widest">
                    {e.country === "france" ? "🇫🇷 France" : "🌍 International"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
